/**
 * Build SticeArcWall extrude geometry from layout sides after drawSite().
 * Matches grainwaveclient SticeArcWall segment order (no re-chain).
 */
import * as THREE from "three";
import * as global from "../../../data/global";

const sideHasDrawSiteLocals = (s) =>
    s &&
    typeof s.lx1 === "number" &&
    Number.isFinite(s.lx1) &&
    typeof s.lz1 === "number" &&
    Number.isFinite(s.lz1) &&
    typeof s.lx2 === "number" &&
    Number.isFinite(s.lx2) &&
    typeof s.lz2 === "number" &&
    Number.isFinite(s.lz2);

/**
 * @param {object[]} layoutSides — one bin's sides in perimeter order
 * @param {object} extrudeSettings
 * @returns {{ geometry: THREE.ExtrudeGeometry|null, floorGeometry: THREE.ExtrudeGeometry|null }}
 */
export const buildSticeWallGeometriesFromLayoutSides = (layoutSides, extrudeSettings) => {
    const all = layoutSides || [];
    const sides = all.filter(sideHasDrawSiteLocals);
    // Skipping walls without drawSite locals would close the shape across the gap and cut
    // corners off the footprint. Let the caller fall back to the catalog rectangle instead.
    if (!sides.length || sides.length !== all.length) {
        return { geometry: null, floorGeometry: null };
    }

    const shape = new THREE.Shape();
    const hole = new THREE.Shape();
    let isShapeMoved = false;

    sides.forEach((layoutSide) => {
        if (!(layoutSide.curveRadius > 0)) {
            if (!isShapeMoved) {
                shape.moveTo(layoutSide.lx1, layoutSide.lz1);
                shape.lineTo(layoutSide.lx2, layoutSide.lz2);
                hole.moveTo(layoutSide.hlx1, layoutSide.hlz1);
                hole.lineTo(layoutSide.hlx2, layoutSide.hlz2);
                isShapeMoved = true;
            } else {
                shape.lineTo(layoutSide.lx2, layoutSide.lz2);
                hole.lineTo(layoutSide.hlx2, layoutSide.hlz2);
            }
        } else {
            let ser = global.getArcStartAndEndRadians(
                layoutSide.lx1,
                layoutSide.lz1,
                layoutSide.lx2,
                layoutSide.lz2,
                layoutSide.lcurveX,
                layoutSide.lcurveZ
            );

            shape.absellipse(
                layoutSide.lcurveX,
                layoutSide.lcurveZ,
                layoutSide.curveRadius,
                layoutSide.curveRadius,
                ser.startRadians,
                ser.endRadians,
                !layoutSide.isInner,
                0
            );

            ser = global.getArcStartAndEndRadians(
                layoutSide.hlx1,
                layoutSide.hlz1,
                layoutSide.hlx2,
                layoutSide.hlz2,
                layoutSide.lcurveX,
                layoutSide.lcurveZ
            );

            hole.absellipse(
                layoutSide.lcurveX,
                layoutSide.lcurveZ,
                layoutSide.newCurveRadius,
                layoutSide.newCurveRadius,
                ser.startRadians,
                ser.endRadians,
                !layoutSide.isInner,
                0
            );
            isShapeMoved = true;
        }
    });

    if (!isShapeMoved) {
        return { geometry: null, floorGeometry: null };
    }

    shape.holes.push(hole);
    let geometry = null;
    let floorGeometry = null;
    try {
        geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        geometry.rotateX(-Math.PI / 2);
        const floor = new THREE.Shape();
        floor.copy(hole);
        floorGeometry = new THREE.ExtrudeGeometry(floor, extrudeSettings);
        floorGeometry.rotateX(-Math.PI / 2);
    } catch {
        geometry = null;
        floorGeometry = null;
    }

    return { geometry, floorGeometry };
};
